import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import EventBus from "../common/EventBus";
import { Link, useNavigate } from "react-router-dom";
import { AppDispatch } from '../store';
import { useAppDispatch } from '../hooks';
import { logout } from "../slices/auth";
import { Toast, ToastContainer } from "react-bootstrap";
import { State } from "../interfaces/types";

interface SubMenuItem {
  path: string;
  icon: React.ReactNode;
  title: string;
  subNav?: SubMenuItem[];
  iconOpened?: React.ReactNode;
  iconClosed?: React.ReactNode;
  hasOnClick?: boolean;
}

interface SubMenuProps {
  item: SubMenuItem;
  onClickMenuItem: () => void;
}

const SubMenu: React.FC<SubMenuProps> = ({ item, onClickMenuItem }) => {
  let navigate = useNavigate();
  const [subnav, setSubnav] = useState<boolean>(item.title === "Administración");
  const showSubnav = () => setSubnav(!subnav);
  const handleMenuClick = (event: React.MouseEvent) => {
    if (item.subNav) {
      event.preventDefault();
      showSubnav();
      return;
    }

    onClickMenuItem();
  };

  const { user: currentUser } = useSelector((state: State) => state.auth);
  const dispatch: AppDispatch = useAppDispatch();

  const [toastMessage] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<boolean>(false);

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    EventBus.on("logout", () => {
      logOut();
    });

    return () => {
      EventBus.remove("logout");
    };
  }, [logOut]);

  const canShowItem = (item: SubMenuItem): boolean => {
    const roles = currentUser?.roles || [];
    const isAdmin = roles.includes("ROLE_ADMIN") || roles.includes("ADMIN");
    const isProvider = roles.includes("ROLE_PROVIDER") || roles.includes("PROVIDER");

    switch (item.title) {
      case "Perfil":
        return isAdmin || isProvider;

      case "Administración":
        return isAdmin;

      default:
        return true;
    }
  };

  const goProfile = () => {
    navigate("/admin-home");
  };
  
  const getOnClick: (title: string) => (e: React.MouseEvent) => void =
  (title: string) => {
    switch (title) {
      case "Salir":
        return logOut;
      case "Perfil":
        return goProfile;
      default:
        return () => {};
    }
  };

  return (
    <>
      <ToastContainer position="bottom-end" className="p-3">
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={4000} 
          autohide 
          bg="success"
          style={{ width: '350px' }}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {item.hasOnClick ? (
        item.title === 'Salir' ? ( <li className="nav-text">
          <a href={item.path} onClick={getOnClick(item.title)}>
            {item.icon}
            <span className="d-sm-inline">{item.title}</span>
          </a>
        </li>) : canShowItem(item) && (<li className="nav-text">
          <a href={item.path} onClick={getOnClick(item.title)}>
            {item.icon}
            <span className="d-sm-inline">{item.title}</span>
          </a>
        </li>)
      ) : canShowItem(item) && (
        <li className="nav-text">
          <Link
            to={item.path}
            onClick={handleMenuClick}
          >
            <div className="nav-with-sub">
              {item.icon}
              <span className="d-sm-inline">
                {item.title}
              </span>
            </div>
            {item.subNav && (
              <div className="nav-with-sub ps-1 d-sm-inline">
                {item.subNav && subnav
                  ? item.iconOpened
                  : item.subNav
                  ? item.iconClosed
                  : null}
              </div>
            )}
          </Link>
        </li>)}
      {canShowItem(item) && subnav &&
        item.subNav?.map((subItem, index) => (
          <li className="nav-text-sub" key={index}>
            <Link to={subItem.path} onClick={onClickMenuItem}>
              {subItem.icon}
              <span className="d-sm-inline">
                {subItem.title}
              </span>
            </Link>
          </li>
        ))
      }
    </>
  );
};

export default SubMenu;
